import { useQuery } from '@apollo/client';
import { useRouter } from 'next/router';
import { useSession } from '@marriott/mi-headless-utils';
import { useClientEnvVarsStore, useUserDetailsStore } from '@marriott/mi-store-utils';
import { Button, canUseDOM, getDirection, Icon } from '@marriott/mi-ui-library';
import clsx from 'clsx';
import { FC, useEffect, useState } from 'react';
import { propertyBarAuthQuery, propertyBarQuery } from '../../../../organisms/NavigationContainer/queries';
import { useNavContext } from '../../../../organisms/NavigationContainer/NavigationContextProvider';
import NavigationDropdownMobile from '../../NavigationDropdown/NavigationDropdownMobile/NavigationDropdownMobile';
import { HWSSecondaryNavigationModel } from '../HWSPrimaryDetachedNavigation.types';
import {
  StyledActiveMenu,
  StyledActiveMenuContainer,
  StyledBackCta,
  StyledHamburgerButton,
  StyledMainMenu,
  StyledMainMenuItem,
  StyledMainMenuItems,
  StyledPropertyHeading,
  StyledSubMenuWithBackCta,
} from './HWSSecondaryNavigationMobile.styles';

const HWSSecondaryNavigationMobile: FC<HWSSecondaryNavigationModel> = props => {
  const { envVarsObject } = useClientEnvVarsStore();
  const locale = envVarsObject?.['ACCEPT_LANGUAGE'];

  const { datalayer, session } = useSession();
  const { isSignedInUser } = useUserDetailsStore(state => ({
    isSignedInUser: state.isSignedInUser,
  }));
  const { setIsMainMenuOpen } = useNavContext();

  const STR_OPEN_MENU = 'Open menu';
  const STR_CLOSE_MENU = 'Close menu';

  const [selectedMenuIndex, setSelectedMenuIndex] = useState<number | null>(null);
  const [showNavigationMenu, setShowNavigationMenu] = useState(false);
  const [direction, setDirection] = useState(false);

  const { query } = useRouter();
  const qpRaw = query['propertyCode'] as string | string[] | undefined;
  const qpPropertyCode = (Array.isArray(qpRaw) ? qpRaw[0] : qpRaw || '').toUpperCase();
  const qpPropertyId = query['propertyId'] as string | undefined;

  useEffect(() => {
    setDirection(getDirection());
  }, []);

  // find the property id from the global store with fallback to datalayer window object
  const propertyId =
    (canUseDOM &&
      ((qpPropertyCode ||
        qpPropertyId ||
        session?.data?.AriesSearch?.searchCriteria?.propertyId ||
        datalayer?.['prop_marsha_code'] ||
        datalayer?.['mrshaCode']) as string | undefined)) ||
    'NYCES';

  const variables: { propertyId: string } = { propertyId: propertyId as string };

  // fetch data hotel data for either signed in or signed out users.
  const { data: propertyBarData } = useQuery(isSignedInUser ? propertyBarAuthQuery : propertyBarQuery, {
    fetchPolicy: isSignedInUser ? 'no-cache' : 'cache-first',
    context: {
      headers: {
        'accept-language': locale,
      },
    },
    skip: !propertyId,
    variables,
    onError: error => console.error('Error fetching property data', error),
  });

  const toggleNavigationMenu = () => {
    if (!showNavigationMenu) {
      document.body.classList.add('no-overflow');
      setIsMainMenuOpen(true);
    } else {
      document.body.classList.remove('no-overflow');
      setSelectedMenuIndex(null);
      setIsMainMenuOpen(false);
    }
    setShowNavigationMenu(!showNavigationMenu);
  };

  return (
    <>
      <StyledHamburgerButton>
        <Button
          id="menuButton"
          className={clsx(`main-nav__btn main-nav__menu-btn d-lg-none main-nav__menu-btn--close`, {
            'hamburger-menu': !showNavigationMenu,
          })}
          ariaExpanded={showNavigationMenu}
          callback={toggleNavigationMenu}
          ariaLabel={showNavigationMenu ? STR_CLOSE_MENU : STR_OPEN_MENU}
        >
          <Icon
            iconClass={`icon-l ${
              showNavigationMenu ? 'main-nav__menu-close-icon icon-clear' : 'main-nav__menu-open-icon icon-menu'
            }`}
          />
        </Button>
      </StyledHamburgerButton>
      <StyledMainMenu
        className={clsx(showNavigationMenu && 'open', selectedMenuIndex !== null && 'submenu-open')}
        aria-label={props?.navigationAriaLabel}
        data-testid="main-menu"
      >
        {selectedMenuIndex !== null && (
          <StyledActiveMenuContainer>
            <StyledBackCta onClick={() => setSelectedMenuIndex(null)}>
              <span className={direction ? `icon-forward-arrow-cropped` : `icon-back-arrow-cropped`}></span>
            </StyledBackCta>
            <StyledActiveMenu>
              {selectedMenuIndex !== null ? (
                <span className="active-menu-name">
                  {props?.hwsSectionNavigationItems?.[selectedMenuIndex]?.menuItemLabel}
                </span>
              ) : (
                <span className="property-name">{propertyBarData?.property?.basicInformation?.name}</span>
              )}
            </StyledActiveMenu>
          </StyledActiveMenuContainer>
        )}

        <StyledMainMenuItems>
          {propertyBarData?.property?.basicInformation?.name && (
            <StyledPropertyHeading>
              <span className="property-heading-text">{propertyBarData?.property?.basicInformation?.name}</span>
            </StyledPropertyHeading>
          )}
          {props?.hwsSectionNavigationItems?.map((mainMenuItem, menuItemIndex: number) => {
            const hasSubMenu = !!mainMenuItem?.sectionNavigationSubItems?.length;

            return (
              <StyledMainMenuItem key={`main-menu-item-${menuItemIndex}`}>
                <Button
                  isLink={!hasSubMenu}
                  href={hasSubMenu ? undefined : mainMenuItem.menuItemPath}
                  callback={hasSubMenu ? () => setSelectedMenuIndex(menuItemIndex) : undefined}
                  trackingProperties={props?.trackingProperties}
                  target={mainMenuItem?.openInNewTab ? '_blank' : '_self'}
                >
                  <span>{mainMenuItem.menuItemLabel}</span>
                  {hasSubMenu && (
                    <span
                      className={`${direction ? `icon-arrow-left` : `icon-arrow-right`} sub-menu-toggler`}
                      data-testid={`sub-menu-toggler-${menuItemIndex}`}
                    ></span>
                  )}
                </Button>

                {mainMenuItem?.enableDropdown && hasSubMenu && (
                  <StyledSubMenuWithBackCta
                    className={clsx(selectedMenuIndex === menuItemIndex && 'open')}
                    data-testid={`sub-menu-${menuItemIndex}`}
                  >
                    <NavigationDropdownMobile
                      menuItemLabel={mainMenuItem.menuItemLabel}
                      ariaLabel={mainMenuItem.menuItemLabel}
                      sectionNavigationSubItems={mainMenuItem?.sectionNavigationSubItems || []}
                      activeMenuItem={props?.activeMenuItem}
                      trackingProperties={props?.trackingProperties}
                      menuItemIndex={menuItemIndex}
                    />
                  </StyledSubMenuWithBackCta>
                )}
              </StyledMainMenuItem>
            );
          })}
        </StyledMainMenuItems>
      </StyledMainMenu>
    </>
  );
};

export default HWSSecondaryNavigationMobile;
